import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
    ReconciliationErrorCodes,
    ReconciliationSummaryResponse,
} from "@common/contracts/reconciliation.contract";
import { FinancialAccount } from "@common/types/financialAccount";
import {
    reconciliationContractClient,
    refreshDocumentsWhenPeriodReconciled,
} from "@api/reconciliation.api";
import { useScope } from "@state/scopeContext.lib";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ReconciliationIncosistentStateErrorToast } from "./ReconciliationIncosistentStateErrorToast";
import { ReconciliationMissingDateOrBalanceErrorToast } from "./ReconciliationMissingDateOrBalanceErrorToast";
import { ReconciliationNonConsecutiveStatementsDatesErrorToast } from "./ReconciliationNonConsecutiveStatementsDatesErrorToast";
import { ReconciliationNonConsecutiveStatementsBalancesErrorToast } from "./ReconciliationNonConsecutiveStatementsBalancesErrorToast";

export function useReconciliationSummaryLoader(
    reconciliedFinancialAccount: FinancialAccount,
) {
    const { workspaceId } = useScope();
    const toaster = useToaster();
    const queryClient = useQueryClient();

    return useCallback(async (): Promise<
        ReconciliationSummaryResponse | undefined
    > => {
        try {
            const response =
                await reconciliationContractClient.getReconciliationSummary({
                    query: {
                        financialAccountId: reconciliedFinancialAccount.id,
                        workspaceId: workspaceId ?? "",
                    },
                });

            return response.body;
        } catch (error) {
            if (
                isAxiosError(error) &&
                error.response?.status === 400 &&
                error.response.data.errorCode
            ) {
                switch (error.response.data.errorCode) {
                    case ReconciliationErrorCodes.RECONCILING_WRONG_DATES:
                        toaster.toast(
                            <ReconciliationIncosistentStateErrorToast
                                error={error.response.data}
                                reconciliedFinancialAccount={
                                    reconciliedFinancialAccount
                                }
                            />,
                        );
                        break;
                    case ReconciliationErrorCodes.STATEMENT_DATE_OR_BALANCE_MISSING:
                        toaster.toast(
                            <ReconciliationMissingDateOrBalanceErrorToast
                                error={error.response.data}
                            />,
                        );
                        break;
                    case ReconciliationErrorCodes.NON_CONSECUTIVE_STATEMENTS_DATES:
                        toaster.toast(
                            <ReconciliationNonConsecutiveStatementsDatesErrorToast
                                error={error.response.data}
                            />,
                        );
                        break;
                    case ReconciliationErrorCodes.NON_CONSECUTIVE_STATEMENTS_BALANCES:
                        toaster.toast(
                            <ReconciliationNonConsecutiveStatementsBalancesErrorToast
                                error={error.response.data}
                            />,
                        );
                        break;
                    case ReconciliationErrorCodes.NO_DOCUMENTS_FOUND:
                        toaster.toast(
                            "No matching documents found for reconciliation. Re-upload your statements.",
                        );
                        break;
                    case ReconciliationErrorCodes.STATEMENT_PERIOD_ALREADY_RECONCILED:
                        await refreshDocumentsWhenPeriodReconciled(queryClient)(
                            error,
                        );
                        break;
                }
                return undefined;
            }
            throw error;
        }
    }, [queryClient, reconciliedFinancialAccount, workspaceId, toaster]);
}
